import { motion } from 'framer-motion';
import { ChevronRight, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button, Card, SectionHeader, useToast } from '../../components';
import {
  selectExpenseCategories,
  selectIncomeCategories,
  useActions,
  useAppData,
} from '../../data';
import type { Category, TransactionType } from '../../data/types';
import { resolveIcon } from '../../lib/icons';
import { springQuick, tapScale } from '../../tokens';
import { CategoryEditSheet } from './CategoryEditSheet';

/**
 * Categories management section. Two collapsible-by-toggle groups
 * (Expense / Income) showing every category as a row with the icon
 * tinted in the category color. Tap a row to edit; the trailing +
 * row opens the add sheet.
 */
export function CategoriesSection() {
  const data = useAppData();
  const actions = useActions();
  const toast = useToast();

  const expense = useMemo(() => selectExpenseCategories(data), [data]);
  const income = useMemo(() => selectIncomeCategories(data), [data]);

  const [sheet, setSheet] = useState<{
    mode: 'add' | Category;
    type: TransactionType;
  } | null>(null);

  return (
    <div className="mb-section-gap">
      <SectionHeader title="Categories" />
      <p className="text-[12px] text-text-secondary mb-3 px-1">
        Tap a category to edit its name, icon, color, or budget tags.
      </p>

      <CategoryGroup
        label="Expense"
        items={expense}
        onEdit={(cat) => setSheet({ mode: cat, type: 'expense' })}
        onAdd={() => setSheet({ mode: 'add', type: 'expense' })}
      />

      <CategoryGroup
        label="Income"
        items={income}
        onEdit={(cat) => setSheet({ mode: cat, type: 'income' })}
        onAdd={() => setSheet({ mode: 'add', type: 'income' })}
        className="mt-card-gap"
      />

      <CategoryEditSheet
        mode={sheet?.mode ?? null}
        defaultType={sheet?.type ?? 'expense'}
        currency={data.profile.currency}
        onClose={() => setSheet(null)}
        onSave={(payload) => {
          if (payload.mode === 'add') {
            actions.addCategory(payload.input);
            toast.show({ message: 'Category added', kind: 'success' });
          } else {
            actions.updateCategory(payload.id, payload.patch);
            toast.show({ message: 'Category updated', kind: 'success' });
          }
        }}
        onDelete={(id) => {
          // Block deletion if any transactions still reference it.
          const inUse = data.transactions.some((t) => t.categoryId === id);
          if (inUse) {
            toast.show({
              message: 'Re-categorize its transactions first',
              kind: 'warning',
            });
            return;
          }
          actions.deleteCategory(id);
          toast.show({ message: 'Category deleted', kind: 'info' });
        }}
      />
    </div>
  );
}

function CategoryGroup({
  label,
  items,
  onEdit,
  onAdd,
  className,
}: {
  label: string;
  items: Category[];
  onEdit: (cat: Category) => void;
  onAdd: () => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary mb-2 px-1">
        {label} · {items.length}
      </div>
      <Card flush elevation="sm" className="py-1">
        {items.map((c, i) => (
          <div key={c.id}>
            {i > 0 ? (
              <div className="h-px bg-surface-secondary mx-card-pad" />
            ) : null}
            <CategoryRow category={c} onClick={() => onEdit(c)} />
          </div>
        ))}
        {items.length > 0 ? (
          <div className="h-px bg-surface-secondary mx-card-pad" />
        ) : null}
        <div className="px-card-pad py-2">
          <Button
            variant="ghost"
            fullWidth
            size="sm"
            leadingIcon={<Plus size={16} strokeWidth={1.5} />}
            onClick={onAdd}
          >
            Add {label.toLowerCase()} category
          </Button>
        </div>
      </Card>
    </div>
  );
}

function CategoryRow({
  category,
  onClick,
}: {
  category: Category;
  onClick: () => void;
}) {
  const Icon = resolveIcon(category.icon);
  const bucket =
    category.bucket503020 === 'needs'
      ? 'Needs'
      : category.bucket503020 === 'wants'
        ? 'Wants'
        : category.bucket503020 === 'savings'
          ? 'Savings'
          : null;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={tapScale}
      transition={springQuick}
      className="w-full flex items-center gap-3 py-3 px-card-pad text-left min-h-tap"
    >
      <div
        className="w-9 h-9 rounded-button shrink-0 inline-flex items-center justify-center text-white shadow-sm"
        style={{ backgroundColor: category.color }}
      >
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium text-text-primary truncate">
          {category.name}
        </div>
        {bucket ? (
          <div className="text-[12px] text-text-secondary mt-0.5">
            {bucket}
          </div>
        ) : null}
      </div>
      <ChevronRight size={18} className="text-text-tertiary shrink-0" />
    </motion.button>
  );
}
